<script lang="ts" setup>
import { useModal } from 'vue-final-modal'

import BasicButton from '~/components/form-elements/BasicButton.vue'
import Checkbox from '~/components/form-elements/Checkbox.vue'
import QuantityCounter from '~/components/form-elements/QuantityCounter.vue'
import ConfirmDeletion from '~/components/modal-windows/ConfirmDeletion.vue'
import Multiplicity from '~/components/products/Multiplicity.vue'
import ProductImage from '~/components/products/ProductImage.vue'
import { useProduct } from '~/composables/product'
import constants from '~/constants'
import ImageType from '~/enums/image-type'
import helpers from '~/helpers'
import type { CartProductData } from '~/types/cart'

const props = defineProps<{
  cartProductData: CartProductData
}>()

const emit = defineEmits<{
  (e: 'deleteProductFromCart', id: string): void
  (e: 'setProductQuantity', id: string, quantity: number): void
  (e: 'toggleProductIsSelected', id: string, isSelected: boolean): void
}>()

const imageTypes = [ImageType.Thumbnail240p, ImageType.Thumbnail360p, ImageType.Thumbnail480p, ImageType.Original]

const { toggleIsInWishList } = useProduct()

const availableProductsCount = computed(() => props.cartProductData.meta.availableProductsCount)
const discountInKopecks = computed(() => props.cartProductData.meta.discountInKopecks)
const isSelected = computed({
  get () {
    return props.cartProductData.meta.isSelected
  },
  set (isSelected) {
    emit('toggleProductIsSelected', props.cartProductData.id, isSelected!)
  }
})
const multiplicity = computed(() => props.cartProductData.attributes.multiplicity ?? 1)
const priceInKopecks = computed(() => helpers.convertRublesToKopecks(helpers.getProductPrice(props.cartProductData)))
const quantity = computed({
  get () {
    return props.cartProductData.meta.quantity
  },
  set (x) {
    const { cartProductData } = props

    if (x !== cartProductData.meta.quantity) {
      emit('setProductQuantity', cartProductData.id, x)
    }

    return x
  }
})

function deleteProductFromCart (id: string): void {
  emit('deleteProductFromCart', id)
}

async function openConfirmDeletionModalWindow (): Promise<void> {
  await useModal({
    component: ConfirmDeletion,
    attrs: {
      id: props.cartProductData.id,
      text: constants.confirmDeletionTexts.cartProduct,
      onConfirmDeletion: deleteProductFromCart
    }
  }).open()
}
</script>

<template>
  <div class="cart-product flex items-center justify-between">
    <div class="cart-product__product flex w-full items-center">
      <Checkbox v-model="isSelected" is-new-design :name="`isProductSelected${cartProductData.id}`" />

      <ProductImage class="mr-3" :image-types="imageTypes" :product-data="cartProductData" />

      <div class="w-full">
        <NuxtLink :to="helpers.getProductPath(cartProductData)">
          {{ cartProductData.attributes.name }}
        </NuxtLink>

        <Multiplicity class="mt-2" :multiplicity="multiplicity" />

        <Notification
          v-if="cartProductData.attributes.warning"
          class="mt-2"
          is-small
          status="warning"
        >
          {{ cartProductData.attributes.warning }}
        </Notification>

        <Notification
          v-if="cartProductData.attributes.isOnlyAvailableInMoscow"
          class="mt-2"
          is-small
          status="warning"
        >
          Доставляется только по&nbsp;Москве.
        </Notification>

        <Notification
          v-if="
            availableProductsCount !== undefined
              && availableProductsCount !== null
              && (availableProductsCount === 0 || availableProductsCount < quantity)
          "
          class="mt-2"
          is-small
          status="warning"
        >
          <template v-if="availableProductsCount === 0">
            Нет в&nbsp;наличии.
          </template>
          <template v-else-if="availableProductsCount < quantity">
            {{ helpers.getDeclension(availableProductsCount, constants.wordForms.left) }} {{ availableProductsCount }}&nbsp;шт.
          </template>
        </Notification>
      </div>
    </div>
    <div class="cart-product__product-state flex items-center justify-between">
      <div class="cart-product__quantity-counter-inner whitespace-nowrap">
        <QuantityCounter v-model="quantity" is-small :multiplicity="multiplicity" />

        <div v-if="quantity > 1" class="cart-product__price-per-piece mt-1 text-sm">
          {{ helpers.formatPrice(priceInKopecks, true) }}&nbsp;₽&nbsp;/ шт.
        </div>
      </div>
      <div class="cart-product__total">
        <PriceWithPromoCode
          class="whitespace-nowrap"
          :discount-in-kopecks="discountInKopecks"
          mode="cart-product"
          :need-to-round-price-with-promo-code="false"
          :product-data="cartProductData"
          :quantity="quantity"
        />
      </div>
      <div class="cart-product__actions flex items-center">
        <BasicButton
          :aria-label="cartProductData.meta.isInWishList ? 'Удалить из\xA0«Избранного»' : 'Добавить в\xA0«Избранное»'"
          :color="cartProductData.meta.isInWishList ? 'red' : 'gray'"
          :icon="cartProductData.meta.isInWishList ? 'outlined/favorite' : 'outlined/favorite-border'"
          is-icon-only
          is-red-on-hover
          is-small
          @click="toggleIsInWishList(cartProductData)"
        />

        <BasicButton
          :aria-label="'Удалить из\xA0корзины'"
          color="gray"
          icon="outlined/delete"
          is-icon-only
          is-red-on-hover
          is-small
          @click="openConfirmDeletionModalWindow"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use 'sass:color';

@use 'assets/css/variables';

.cart-product {
  $maxWidth: 600px;

  @media screen and (max-width: $maxWidth) {
    align-items: flex-start;
    flex-direction: column;
  }

  &:not(:last-child) {
    border-bottom: 1px solid color.mix(variables.$main-gray-color, white, 20%);
    margin-bottom: 8px;
    padding: 0 0 8px;
  }

  &__actions {
    @media screen and (max-width: $maxWidth) {
      flex-basis: 0;
      flex-grow: 1;
      justify-content: flex-end;
      padding-left: 12px !important;
    }

    > *:not(:last-child) {
      margin-right: 16px;
    }
  }

  &__price-per-piece {
    color: color.mix(variables.$main-gray-color, white, 60%);
  }

  &__product {
    @media screen and (max-width: $maxWidth) {
      margin-bottom: 8px;
    }
  }

  &__product-state {
    @media screen and (max-width: $maxWidth) {
      width: 100%;
    }

    > * {
      padding: 8px 12px;

      @media screen and (max-width: $maxWidth) {
        padding: 0;
      }
    }
  }

  &__quantity-counter-inner {
    min-width: 100px;

    @media screen and (max-width: $maxWidth) {
      flex-basis: 0;
      flex-grow: 1;
      min-width: auto;
      padding-right: 12px;
    }
  }

  &__total {
    min-width: 150px;

    @media screen and (max-width: $maxWidth) {
      display: flex;
      justify-content: center;
      min-width: auto;
      padding: 0 12px;
      width: 40%;
    }
  }
}
</style>
